import Skeleton from '@mui/material/Skeleton'
import React, { memo, useEffect, useMemo, useState } from 'react'

import RenderException from 'components/work-order/details/exceptions/RenderException'
import { EXCEPTION_TYPES } from 'settings/constants/exceptions'
import { loadExceptionImageBase64, resolveExceptionImageFileLocation } from 'utils/exceptionImage'

import type { SxProps, Theme } from '@mui/material/styles'
import type { ExceptionImageEntry, ExceptionImageSize, ExceptionMediaSlide } from 'utils/exceptionImage'

type Props = {
  exception: { exceptionType?: string | null; fileLocation?: string | null; images?: ExceptionImageEntry[] | null }
  photo?: ExceptionMediaSlide | ExceptionImageEntry | null
  height?: string | number
  ImageSx?: SxProps<Theme>
  skeletonSx?: SxProps<Theme>
  size?: ExceptionImageSize
  withSwiperSlide?: boolean
  PageProps?: Record<string, unknown>
}

const ignoreLoadError = () => undefined

const ExceptionImage: React.FC<Props> = ({
  exception,
  photo,
  height = '194px',
  ImageSx,
  skeletonSx,
  size = 'original',
  withSwiperSlide = false,
  PageProps,
}) => {
  const variantPath = useMemo(() => resolveExceptionImageFileLocation(photo, exception, size), [photo, exception, size])
  const basePath = useMemo(() => resolveExceptionImageFileLocation(photo, exception, 'original'), [photo, exception])
  const { exceptionType } = exception
  const isSignExceptionType = useMemo(() => exceptionType === EXCEPTION_TYPES.Sign, [exceptionType])
  const [base64Data, setBase64Data] = useState('')
  const [isPhotoLoading, setIsPhotoLoading] = useState(false)

  const showContent = base64Data && !isPhotoLoading
  const showSkeleton = !base64Data || isPhotoLoading

  useEffect(() => {
    if (!variantPath && !basePath) {
      setBase64Data('')
      return undefined
    }

    let cancelled = false
    setIsPhotoLoading(true)

    loadExceptionImageBase64(variantPath, basePath, size)
      .then(base64 => {
        if (!cancelled && base64) {
          setBase64Data(base64)
        }
      })
      // Missing/stale storage objects are expected for legacy exceptions; the skeleton stays.
      .catch(ignoreLoadError)
      .finally(() => {
        if (!cancelled) {
          setIsPhotoLoading(false)
        }
      })

    return () => {
      cancelled = true
    }
  }, [variantPath, basePath, size])

  if (withSwiperSlide) {
    return (
      <swiper-slide lazy="false">
        {showContent && (
          <RenderException
            base64Data={base64Data}
            height={height}
            ImageSx={ImageSx}
            isSignExceptionType={isSignExceptionType}
            PageProps={PageProps}
          />
        )}
        {showSkeleton && (
          <Skeleton sx={[{ width: '100%', height }, ...(Array.isArray(skeletonSx) ? skeletonSx : [skeletonSx])]} variant="rectangular" />
        )}
      </swiper-slide>
    )
  }

  return (
    <>
      {showContent && (
        <RenderException
          base64Data={base64Data}
          height={height}
          ImageSx={ImageSx}
          isSignExceptionType={isSignExceptionType}
          PageProps={PageProps}
        />
      )}
      {isPhotoLoading && (
        <Skeleton sx={[{ width: '100%', height }, ...(Array.isArray(skeletonSx) ? skeletonSx : [skeletonSx])]} variant="rectangular" />
      )}
    </>
  )
}

export default memo(ExceptionImage)
